import { delay } from 'redux-saga'
import { takeEvery, takeLatest, call, put } from 'redux-saga/effects'

import { SubmissionError } from 'redux-form'

import {
  updatePhoneVerifyProgress,
  errorPhoneVerify,
  ACCOUNT_PHONE_VERIFY_GENERATE,
  ACCOUNT_PHONE_VERIFY_REGENERATE,
  ACCOUNT_PHONE_VERIFY_CONFIRM,
} from 'actions/verify-phone'

function _generatePhoneVerifyCode({ create }, logError) {
  return function*(action) {
    try {
      const { phone, username, orgslug, resolve } = action.payload

      const contactMethodEndpoint = `/api/v1/org/${orgslug}/profile/${username}/methods`
      const contactMethodPayload = {
        label: 'Phone',
        value: phone,
        sendIntroSMS: true,
      }
      const contactResponse = yield call(
        create,
        contactMethodEndpoint,
        contactMethodPayload
      )
      const contactId = contactResponse.id
      const verified = contactResponse.verified === 'verified'

      if (verified) {
        yield put(
          updatePhoneVerifyProgress({
            phone,
            contactId,
            verified,
          })
        )
        yield call(resolve)
      } else {
        const verifyEndpoint = `${contactMethodEndpoint}/phone/${contactId}/verified`
        const verifyPayload = {
          phone_confirmation_type: 'phone',
        }
        const verificationResponse = yield call(
          create,
          verifyEndpoint,
          verifyPayload
        )
        const attemptId = verificationResponse.attempt_id

        yield put(
          updatePhoneVerifyProgress({
            phone,
            contactId,
            attemptId,
            verified,
          })
        )
        yield call(resolve)
      }
    } catch (err) {
      const { reject } = action.payload

      yield call(logError, err)
      const data =
        err.status === 400 && err.responseText
          ? JSON.parse(err.responseText)
          : null

      if (data) {
        const errorMessage = data[0].errors[0].includes(
          'Invalid phone number or email address'
        )
          ? 'Invalid phone number.'
          : data[0].errors[0]
        yield call(reject, new SubmissionError({ phone: errorMessage }))
      } else {
        yield call(
          reject,
          new SubmissionError({
            phone: 'Something went wrong. Please try again or skip this step.',
          })
        )
      }

      yield put(
        errorPhoneVerify({
          error: true,
          data: data,
        })
      )
    }
  }
}

function _regeneratePhoneVerifyCode({ create }, logError) {
  return function*(action) {
    try {
      yield call(delay, 1000)
      const { phone, username, orgslug, contactId } = action.payload

      const verifyEndpoint = `/api/v1/org/${orgslug}/profile/${username}/methods/phone/${contactId}/verified`
      const verifyPayload = {
        phone_confirmation_type: 'phone',
      }
      const verificationResponse = yield call(
        create,
        verifyEndpoint,
        verifyPayload
      )
      const attemptId = verificationResponse.attempt_id
      const responseStatus = verificationResponse.status
      yield put(
        updatePhoneVerifyProgress({
          phone: phone,
          contactId: contactId,
          attemptId: attemptId,
          responseStatus: responseStatus,
        })
      )
    } catch (err) {
      yield call(logError, err)
      const data =
        err.status === 400 && err.responseText
          ? JSON.parse(err.responseText)
          : null
      yield put(
        errorPhoneVerify({
          error: true,
          data: data,
        })
      )
    }
  }
}

function _confirmPhoneVerifyCode({ update }, logError) {
  return function*(action) {
    try {
      const { contactId, attemptId, code, orgslug, username } = action.payload
      const verifyPhoneEndpoint = `/api/v1/org/${orgslug}/profile/${username}/methods/phone/${contactId}/verified`
      const verifyPhonePayload = {
        attempt_id: attemptId,
        code: code,
        sendIntroSMS: true,
      }

      const verifyPhoneResponse = yield call(
        update,
        verifyPhoneEndpoint,
        verifyPhonePayload
      )
      const verified = verifyPhoneResponse.status === 'verified'
      yield put(updatePhoneVerifyProgress({ verified: verified }))

      if (verified) {
        const updatePagingPolicyEndpoint = `/api/v1/org/${orgslug}/profile/${username}/policy/0/0`
        const pagingPolicyPayload = {
          contact: {
            type: 'phone',
            id: contactId,
          },
          index: 0,
          type: 'sms',
        }
        yield call(update, updatePagingPolicyEndpoint, pagingPolicyPayload)
      }
    } catch (err) {
      yield call(logError, err)
      const data =
        err.status === 400 && err.responseText
          ? JSON.parse(err.responseText)
          : null
      yield put(
        errorPhoneVerify({
          error: true,
          data: data,
        })
      )
    }
  }
}

export const Test = {
  _generatePhoneVerifyCode,
  _regeneratePhoneVerifyCode,
  _confirmPhoneVerifyCode,
}

// Watchers
// ---------------------------------------------------------------------------
export function* watchGeneratePhoneVerify(api, error) {
  yield takeEvery(
    ACCOUNT_PHONE_VERIFY_GENERATE,
    _generatePhoneVerifyCode(api, error)
  )
}

export function* watchRegeneratePhoneVerify(api, error) {
  yield takeLatest(
    ACCOUNT_PHONE_VERIFY_REGENERATE,
    _regeneratePhoneVerifyCode(api, error)
  )
}

export function* watchConfirmPhoneVerify(api, error) {
  yield takeEvery(
    ACCOUNT_PHONE_VERIFY_CONFIRM,
    _confirmPhoneVerifyCode(api, error)
  )
}
